import { useState } from 'react'
import { Package, PencilSimple, Plus, Power, Trash } from '@phosphor-icons/react'
import { Badge, Banner, ChoiceGroup, DataTable, EmptyState, Field, Input, type Column } from '@/ui'
import { useCan } from '@/auth/context'
import { errorMessage } from '../api/core'
import { gramsToKgString } from '../api/mappers'
import { useDeleteSackType, useSackTypes, useSaveSackType, type SackTypeRow } from '../api/setup'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage } from '../components/Layout'
import { Code, Kg } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'
import {
  createSackTypeBody,
  draftFromSackType,
  emptySackTypeDraft,
  normaliseCode,
  sackTypeOk,
  switchUnit,
  updateSackTypeBody,
  validateSackTypeDraft,
  weightToGrams,
  type SackTypeDraft,
  type WeightUnit,
} from '../model/sackTypeForm'
import { serverProblem } from '../model/serverError'

const LIMIT = 100

type Editing = { mode: 'create' } | { mode: 'edit'; row: SackTypeRow }

/** Sack types (F4.6-a): known sacks and their empty weight, taken off every bag's weight. */
export function SackTypesPage() {
  const [f, setF, { reset, active }] = useUrlState({ use: '', page: 1 })
  // uc-core reads ?active=false as true (implicit conversion runs before its toBool), so "not
  // offered" is filtered here from the full list; there are only a handful of sack types.
  const q = useSackTypes({ page: f.page, limit: LIMIT, active: f.use === 'yes' ? true : undefined })
  const save = useSaveSackType()
  const can = useCan()
  const [editing, setEditing] = useState<Editing | null>(null)
  const [removing, setRemoving] = useState<SackTypeRow | null>(null)
  const all = q.data?.rows ?? []
  const rows = f.use === 'no' ? all.filter((r) => !r.active) : all

  const columns: Column<SackTypeRow>[] = [
    { id: 'code', header: 'Code', cell: (r) => <Code>{r.code}</Code>, sortValue: (r) => r.code },
    { id: 'name', header: 'Name', cell: (r) => r.name, sortValue: (r) => r.name },
    {
      id: 'weight',
      header: 'Empty sack weight',
      align: 'right',
      sortValue: (r) => r.tareG,
      cell: (r) => (
        <span className="flex flex-col items-end leading-tight">
          <span className="font-mono font-semibold tabular">{r.tareG.toLocaleString('en-IN')} g</span>
          <Kg grams={r.tareG} className="text-[12px] text-muted" />
        </span>
      ),
    },
    {
      id: 'active',
      header: 'Offered on phones',
      cell: (r) => (r.active ? <Badge tone="ok">In use</Badge> : <Badge tone="neutral">Not offered</Badge>),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <ActionButton
            perm="sack-types:update"
            hide
            size="sm"
            variant="ghost"
            leadingIcon={<Power />}
            loading={save.isPending && save.variables?.id === r.id}
            onClick={() => save.mutate({ id: r.id, body: { active: !r.active } })}
          >
            {r.active ? 'Stop offering' : 'Offer again'}
          </ActionButton>
          <ActionButton perm="sack-types:update" hide size="sm" variant="ghost" leadingIcon={<PencilSimple />} onClick={() => setEditing({ mode: 'edit', row: r })}>
            Edit
          </ActionButton>
          <ActionButton perm="sack-types:delete" hide size="sm" variant="ghost" leadingIcon={<Trash />} onClick={() => setRemoving(r)}>
            Delete
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Sack types"
      description="The sacks farmers bring and what each weighs empty. The operator picks the sack on the phone and its weight is taken off every bag, so farmers are paid for produce, not the sack."
      actions={
        <ActionButton perm="sack-types:create" leadingIcon={<Plus />} onClick={() => setEditing({ mode: 'create' })}>
          Add sack type
        </ActionButton>
      }
    >
      <DataTable<SackTypeRow>
        caption="Sack types"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={can('sack-types:update') ? (r) => setEditing({ mode: 'edit', row: r }) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: f.use === 'no' ? rows.length : (q.data?.total ?? 0), onPageChange: (page) => setF({ page }) }}
        empty={
          <EmptyState
            icon={<Package />}
            title={active ? 'No sack types match' : 'No sack types yet'}
            description="Weigh ten empty sacks of a kind and enter the average."
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Offered on phones"
              value={f.use as 'yes' | 'no' | ''}
              onChange={(use) => setF({ use })}
              options={[
                { value: 'yes', label: 'In use' },
                { value: 'no', label: 'Not offered' },
              ]}
            />
          </FilterBar>
        }
      />
      {editing && <SackTypeDialog editing={editing} onClose={() => setEditing(null)} />}
      <DeleteSackType row={removing} onClose={() => setRemoving(null)} />
    </ConsolePage>
  )
}

function SackTypeDialog({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const [d, setD] = useState<SackTypeDraft>(editing.mode === 'edit' ? draftFromSackType(editing.row) : emptySackTypeDraft())
  const [touched, setTouched] = useState<Set<keyof SackTypeDraft>>(new Set())
  const save = useSaveSackType({ inline: true })
  const p = validateSackTypeDraft(d, editing.mode)
  const touch = (k: keyof SackTypeDraft) => setTouched((s) => new Set(s).add(k))
  const shown = (k: keyof SackTypeDraft, msg?: string) => (touched.has(k) ? msg : undefined)
  const grams = weightToGrams(d.weight, d.unit)
  const err = save.isError ? serverProblem(save.error) : null
  const edit = editing.mode === 'edit' ? editing.row : null
  const weightChanged = edit !== null && grams !== null && grams !== edit.tareG

  return (
    <FormDialog
      open
      onClose={onClose}
      title={edit ? `Edit ${edit.code}` : 'Add sack type'}
      saveLabel={edit ? 'Save' : 'Add sack type'}
      loading={save.isPending}
      canSave={sackTypeOk(p)}
      onSave={() =>
        save.mutateAsync(edit ? { id: edit.id, body: updateSackTypeBody(d) } : { body: createSackTypeBody(d) })
      }
    >
      {err && (
        <Banner tone="error" title={err.message}>
          {err.details.join(' · ')}
        </Banner>
      )}
      {edit ? (
        <Field label="Code" hint="The code cannot change.">
          <Input value={d.code} disabled className="font-mono" />
        </Field>
      ) : (
        <Field label="Code" required error={shown('code', p.code)} hint="Short, e.g. PP-50 — capital letters, digits, dashes">
          <Input
            value={d.code}
            onChange={(e) => setD({ ...d, code: normaliseCode(e.target.value) })}
            onBlur={() => touch('code')}
            className="font-mono"
            maxLength={20}
            placeholder="PP-50"
            data-autofocus
          />
        </Field>
      )}
      <Field label="Name" required error={shown('name', p.name)}>
        <Input
          value={d.name}
          onChange={(e) => setD({ ...d, name: e.target.value })}
          onBlur={() => touch('name')}
          maxLength={60}
          placeholder="Plastic (PP) sack, 50 kg"
          data-autofocus={edit ? true : undefined}
        />
      </Field>
      <Field
        label="Empty sack weight"
        required
        error={shown('weight', p.weight)}
        hint={
          grams !== null && !p.weight
            ? `${grams.toLocaleString('en-IN')} g = ${gramsToKgString(grams)} kg`
            : 'Weigh ten empty sacks and enter the average.'
        }
      >
        <div className="flex gap-2">
          <Input
            inputMode={d.unit === 'g' ? 'numeric' : 'decimal'}
            value={d.weight}
            onChange={(e) => setD({ ...d, weight: e.target.value })}
            onBlur={() => touch('weight')}
            placeholder={d.unit === 'g' ? '120' : '0.120'}
            trailing={d.unit}
            className="font-mono"
          />
          <div className="w-36 shrink-0">
            <ChoiceGroup<WeightUnit>
              label="Unit"
              value={d.unit}
              onChange={(unit) => setD(switchUnit(d, unit))}
              options={[
                { value: 'g', label: 'grams' },
                { value: 'kg', label: 'kg' },
              ]}
            />
          </div>
        </div>
      </Field>
      {weightChanged && (
        <Banner tone="info">
          The new weight applies to bags tagged from now on. Bags already weighed keep the weight they were weighed with.
        </Banner>
      )}
    </FormDialog>
  )
}

function DeleteSackType({ row, onClose }: { row: SackTypeRow | null; onClose: () => void }) {
  const del = useDeleteSackType()
  return (
    <ConfirmDialog
      open={Boolean(row)}
      onClose={onClose}
      title={`Delete ${row?.code ?? ''}?`}
      description="It disappears from the phones with their next farmer list update. Bags already weighed with it keep their weight."
      confirmLabel="Delete sack type"
      tone="danger"
      loading={del.isPending}
      onConfirm={async () => {
        if (!row) return
        await del.mutateAsync(row.id)
        onClose()
      }}
    >
      <p className="text-[14px] text-muted">To keep it for later, use “Stop offering” instead.</p>
    </ConfirmDialog>
  )
}
